/**
 * Campaign attribution.
 *
 * The business needs to know which advertisement produced a registration and,
 * later, a payment — and those two things happen on different days, in different
 * sessions. So the UTM parameters on the landing URL are captured once, on the
 * first page of the first visit, and then carried forward.
 *
 * Two records are kept:
 *  - `first`   the very first campaign that ever brought this browser here. Never
 *              overwritten. This is what a purchase is credited to.
 *  - `last`    the most recent campaign, refreshed whenever a URL carries UTMs.
 *              Useful for understanding re-engagement.
 *
 * Storage is first-party `localStorage`, no cookies, no third parties, and it is
 * written regardless of consent because it holds no identifiers — only the
 * campaign labels the business itself put on its own links. It is only ever
 * *transmitted* through the analytics layer, which is consent-gated.
 *
 * ## Two kinds of field, kept apart on purpose
 *
 * A `utm_campaign` is a label the business wrote. A `gclid` is an identifier an
 * advertising company issued for a particular human being's click. They are not
 * the same kind of data and they do not go to the same places:
 *
 *  - `getAttributionForEvent()` returns the labels, and feeds the vendor pixels.
 *  - `getTouchPayload()` returns everything including the click ids, and feeds
 *    exactly one destination — our own `/api/attribution/touch`, which is what
 *    later lets a server-confirmed purchase be reported back to the platform
 *    that earned it.
 *
 * Nothing else may read the click ids, and they are never appended to an
 * outbound URL by `withAttribution`.
 */

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;

  /**
   * The advertisement's own identifiers, populated by the ad platform's URL
   * macros rather than typed by hand — see `marketing/paid-ads/README.md` for
   * the exact template each platform needs.
   *
   * These matter because spend is exported keyed by id while conversions arrive
   * keyed by whatever label somebody typed. Without the ids, cost-per-lead can
   * only be computed at whatever granularity the naming convention survived.
   */
  campaign_id?: string;
  adset_id?: string;
  ad_id?: string;
  creative_id?: string;

  /** Google Ads click id, set by auto-tagging; the only reliable Ads join key. */
  gclid?: string;
  /**
   * Google's newer click ids. `gbraid` replaces `gclid` on iOS app-to-web
   * traffic and `wbraid` on web-to-app; a campaign can return one, both or
   * neither, and treating a missing one as an error would discard real clicks.
   */
  gbraid?: string;
  wbraid?: string;
  /** Meta and TikTok click ids, used by their conversion APIs. */
  fbclid?: string;
  ttclid?: string;

  /** Document referrer at capture time, for traffic with no tagging at all. */
  referrer?: string;
  /** The page the visitor actually landed on. */
  landing_page?: string;
  capturedAt?: string;
}

const FIRST_KEY = 'mdx-attribution-first';
const LAST_KEY = 'mdx-attribution-last';

/**
 * The campaign labels. Safe to send to a vendor, safe to put on an outbound
 * URL, safe to show on a dashboard.
 *
 * `gclid`, `fbclid` and `ttclid` are in this list and were before this file
 * learned about the others, because the vendor tags themselves read them off
 * the URL anyway — passing them back is telling Google about a Google click.
 * The two `braid` ids are deliberately *not* here: nothing in the browser
 * consumes them, so widening the vendor payload to carry them would be sending
 * an identifier further than it needs to go for no measurement gain.
 */
const PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'ttclid',
] as const;

/**
 * Every field read off the landing URL, and the query parameters each may
 * arrive under.
 *
 * The aliases are not indulgence — they are what lets a platform's own macro be
 * pasted in unchanged. Google Ads' tracking template naturally produces
 * `campaignid`; GA4's own convention is `utm_id`; a hand-built link is likely
 * to say `campaign_id`. All three mean the same thing and all three are
 * accepted, so that a mistyped link degrades to a missing id rather than to a
 * silently wrong one.
 */
const CAPTURE: Array<[keyof Attribution, readonly string[]]> = [
  ['utm_source', ['utm_source']],
  ['utm_medium', ['utm_medium']],
  ['utm_campaign', ['utm_campaign']],
  ['utm_content', ['utm_content']],
  ['utm_term', ['utm_term']],
  ['campaign_id', ['utm_id', 'mdx_campaign', 'campaign_id', 'campaignid']],
  ['adset_id', ['mdx_adset', 'adset_id', 'adgroupid', 'adgroup_id']],
  ['ad_id', ['mdx_ad', 'ad_id', 'adid']],
  ['creative_id', ['mdx_creative', 'creative_id', 'creative']],
  ['gclid', ['gclid']],
  ['gbraid', ['gbraid']],
  ['wbraid', ['wbraid']],
  ['fbclid', ['fbclid']],
  ['ttclid', ['ttclid']],
];

/** The subset that identifies a person's click rather than our own campaign. */
const CLICK_ID_FIELDS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid'] as const;

/**
 * A macro the ad platform failed to expand.
 *
 * Every platform's tracking template is a string with placeholders in it, and
 * every platform occasionally serves the placeholder itself — an unexpanded
 * `{campaignid}` or `__CID__` reaching us as a literal. Stored, it becomes a
 * campaign id that matches nothing, joins to no spend row, and looks exactly
 * like real data on a dashboard. Dropped, the touch is simply missing an id,
 * which is true.
 */
const UNEXPANDED = /^(\{\{?.+\}?\}|__[A-Z_]+__|\$\{.+\})$/;

const readKey = (key: string): Attribution | null => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
};

const write = (key: string, value: Attribution): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable — attribution degrades to session-only. Not worth failing over.
  }
};

/**
 * Reads campaign parameters off the current URL. Call once per page load, before
 * anything strips the query string.
 */
export const captureAttribution = (): void => {
  if (typeof window === 'undefined') return;

  const query = new URLSearchParams(window.location.search);
  const captured: Attribution = {};

  CAPTURE.forEach(([field, aliases]) => {
    for (const alias of aliases) {
      const value = query.get(alias)?.trim();
      if (!value || UNEXPANDED.test(value)) continue;
      (captured as Record<string, string>)[field] = value.slice(0, 200);
      return;
    }
  });

  const hasCampaign = Object.keys(captured).length > 0;
  const existingFirst = readKey(FIRST_KEY);

  // An untagged visit still tells us something if it is the very first one:
  // the referrer is the only signal organic and social traffic leaves behind.
  if (!hasCampaign && existingFirst) return;

  const record: Attribution = {
    ...captured,
    referrer: document.referrer || undefined,
    landing_page: window.location.pathname,
    capturedAt: new Date().toISOString(),
  };

  if (!existingFirst) write(FIRST_KEY, record);
  if (hasCampaign) write(LAST_KEY, record);
};

export const getFirstTouch = (): Attribution => (typeof window === 'undefined' ? {} : readKey(FIRST_KEY) ?? {});
export const getLastTouch = (): Attribution => (typeof window === 'undefined' ? {} : readKey(LAST_KEY) ?? {});

/**
 * The attribution fields to attach to a conversion event. Last touch wins for the
 * campaign labels — it is what the ad platforms themselves will report — with
 * first touch kept alongside under a prefix so the two can be compared.
 */
export const getAttributionForEvent = (): Record<string, string> => {
  const first = getFirstTouch();
  const last = getLastTouch();
  const source = Object.keys(last).length > 0 ? last : first;

  const out: Record<string, string> = {};
  PARAMS.forEach((param) => {
    if (source[param]) out[param] = source[param] as string;
  });
  if (first.utm_campaign) out.first_touch_campaign = first.utm_campaign;
  if (first.utm_source) out.first_touch_source = first.utm_source;
  return out;
};

/**
 * Whether this browser is carrying anything worth recording as a touch.
 *
 * A visit with no campaign labels, no click id and no referrer is somebody
 * typing the address in. Posting a row for it would fill the touch log with
 * millions of "direct" entries whose only content is a timestamp, and make the
 * "unattributed" figure on the dashboard look like data rather than the absence
 * of it.
 */
export const hasAttribution = (record: Attribution): boolean =>
  CAPTURE.some(([field]) => Boolean(record[field]));

/**
 * The full record for our own touch endpoint, click ids included.
 *
 * The only caller is `recordTouch` in `lib/touch.ts`. Everything else must use
 * `getAttributionForEvent`.
 */
export const getTouchPayload = (): (Attribution & { hasClickId: boolean }) | null => {
  const last = getLastTouch();
  const first = getFirstTouch();
  const record = Object.keys(last).length > 0 ? last : first;

  if (!hasAttribution(record) && !record.referrer) return null;

  return {
    ...record,
    hasClickId: CLICK_ID_FIELDS.some((field) => Boolean(record[field])),
  };
};

/**
 * Appends the stored campaign parameters to an outbound URL. Use when handing off
 * to a flow that would otherwise lose them (a hosted checkout, an external form).
 */
export const withAttribution = (url: string): string => {
  const attribution = getAttributionForEvent();
  if (Object.keys(attribution).length === 0) return url;
  const target = new URL(url, window.location.origin);
  Object.entries(attribution).forEach(([key, value]) => target.searchParams.set(key, value));
  return target.toString();
};
